import { supabase } from "./supabase";

/** ふたりで共有するプロフィール項目(「好きな食べ物」など) */
export interface ProfileItem {
  id: string;
  label: string;
}

/** 項目への答え。1項目につき1人1つ */
export interface ProfileAnswer {
  item_id: string;
  user_id: string;
  value: string;
}

/** 前後の空白を落とし、空なら無効とみなす */
export function normalizeProfileText(raw: string): string | null {
  const text = raw.trim();
  return text === "" ? null : text;
}

/** 項目の一覧。作った順に出す */
export async function fetchProfileItems(): Promise<ProfileItem[]> {
  const { data, error } = await supabase
    .from("profile_items")
    .select("id, label")
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data;
}

/** 項目を作る。既にあれば作らずにそれを返す(タグと同じ扱い) */
export async function createProfileItem(label: string): Promise<ProfileItem> {
  const { data, error } = await supabase
    .from("profile_items")
    .upsert({ label }, { onConflict: "couple_id,label" })
    .select("id, label")
    .single();
  if (error) throw error;
  return data;
}

/** 項目を消す。ふたりの答えも連鎖して消える */
export async function deleteProfileItem(id: string): Promise<void> {
  const { error } = await supabase.from("profile_items").delete().eq("id", id);
  if (error) throw error;
}

/** ふたりぶんの答えを全部。誰のどの項目かは item_id / user_id で引く */
export async function fetchProfileAnswers(): Promise<ProfileAnswer[]> {
  const { data, error } = await supabase.from("profile_answers").select("item_id, user_id, value");
  if (error) throw error;
  return data;
}

/**
 * 自分の答えを保存する。空にしたら行ごと消す(空文字の「回答済み」を作らない)。
 * user_id は DB 側の既定値(auth.uid())で埋まり、RLS が本人の行しか触らせない。
 */
export async function saveProfileAnswer(itemId: string, raw: string): Promise<void> {
  const value = normalizeProfileText(raw);
  if (value === null) {
    const { error } = await supabase.from("profile_answers").delete().eq("item_id", itemId);
    if (error) throw error;
    return;
  }
  const { error } = await supabase
    .from("profile_answers")
    .upsert({ item_id: itemId, value }, { onConflict: "item_id,user_id" });
  if (error) throw error;
}

/** カップル二人ぶんのプロフィール。閲覧は二人とも、編集は本人の行だけ */
export interface CoupleProfile {
  id: string;
  displayName: string;
  birthDate: string | null;
  phone: string | null;
  address: string | null;
  contactEmail: string | null;
  /** 縮小済みアイコンの data URL(→ lib/avatar.ts) */
  avatar: string | null;
  /** 予定表の色分けに使う `#rrggbb`。未選択なら null(→ lib/color.ts で既定色にフォールバック) */
  favoriteColor: string | null;
}

interface CoupleProfileRow {
  id: string;
  display_name: string;
  birth_date?: string | null;
  phone_number?: string | null;
  address?: string | null;
  contact_email?: string | null;
  avatar?: string | null;
  favorite_color?: string | null;
}

/**
 * ふたりぶんのプロフィール。RLS の profiles_select が同じカップルの全員を返す。
 * マイグレーション適用前の環境でも壊れないよう、列を名指しせずに取る。
 */
export async function fetchCoupleProfiles(): Promise<CoupleProfile[]> {
  const { data, error } = await supabase.from("profiles").select("*");
  if (error) throw error;
  const rows = data as CoupleProfileRow[];
  return rows.map((row) => ({
    id: row.id,
    displayName: row.display_name,
    birthDate: row.birth_date ?? null,
    phone: row.phone_number ?? null,
    address: row.address ?? null,
    contactEmail: row.contact_email ?? null,
    avatar: row.avatar ?? null,
    favoriteColor: row.favorite_color ?? null,
  }));
}

/** 自分のプロフィールを部分更新する。null は「消す」(緊急連絡先を空に戻す) */
export interface ProfilePatch {
  display_name?: string;
  birth_date?: string;
  phone_number?: string | null;
  address?: string | null;
  contact_email?: string | null;
  avatar?: string;
  favorite_color?: string;
}

export async function updateMyProfile(userId: string, patch: ProfilePatch): Promise<void> {
  const { error } = await supabase.from("profiles").update(patch).eq("id", userId);
  if (error) throw error;
}
